import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, AlertCircle, AlertTriangle, CheckCircle2, Info, Lightbulb, Loader2, RefreshCw } from 'lucide-react';

const SEVERITY_CONFIG = {
  success: { color: 'border-state-success bg-green-50', textColor: 'text-state-success', icon: CheckCircle2 },
  warning: { color: 'border-state-warning bg-yellow-50', textColor: 'text-state-warning', icon: AlertTriangle },
  error: { color: 'border-state-error bg-red-50', textColor: 'text-state-error', icon: AlertCircle },
  info: { color: 'border-accent bg-orange-50', textColor: 'text-accent', icon: Lightbulb },
  high: { color: 'border-state-error bg-red-50', textColor: 'text-state-error', icon: AlertCircle },
  medium: { color: 'border-state-warning bg-yellow-50', textColor: 'text-state-warning', icon: AlertTriangle },
  low: { color: 'border-state-info bg-blue-50', textColor: 'text-state-info', icon: Info },
};

export const AIInsightsPanel = ({ endpoint, params = {}, title = 'AI Insights', testId }) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cache, setCache] = useState(null);

  const fetchInsights = useCallback(async ({ refresh = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const q = { ...params };
      if (refresh) q.refresh = 'true';
      const queryString = new URLSearchParams(q).toString();
      const url = queryString ? `${endpoint}?${queryString}` : endpoint;
      const res = await axios.get(url);
      setInsights(res.data.insights || []);
      setCache(res.data.cache || null);
    } catch (err) {
      setError('Could not load AI insights');
    } finally {
      setLoading(false);
    }
  }, [endpoint, JSON.stringify(params)]);   // eslint-disable-line

  useEffect(() => {
    if (endpoint) fetchInsights();
  }, [fetchInsights, endpoint]);

  return (
    <div className="bg-white border border-border rounded-md p-5" data-testid={testId || 'ai-insights-panel'}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Sparkles className="h-5 w-5 text-accent" />
          <h3 className="text-lg font-semibold text-primary">{title}</h3>
        </div>
        <div className="flex items-center gap-3">
          {cache?.hit && (
            <span
              className="text-[10px] uppercase tracking-wider text-text-muted"
              title={`Served from cache · expires in ${Math.max(0, Math.round((cache.expires_in_seconds || 0) / 60))} min`}
              data-testid={`${testId || 'ai-insights-panel'}-cache-hit`}
            >
              cached
            </span>
          )}
          <button
            type="button"
            onClick={() => fetchInsights({ refresh: true })}
            disabled={loading}
            className="text-xs font-semibold text-primary hover:underline disabled:opacity-50 flex items-center gap-1"
            data-testid={`${testId || 'ai-insights-panel'}-refresh`}
            title="Force re-generation (counts against your AI rate limit)"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-accent" />}
        </div>
      </div>

      {loading && insights.length === 0 && (
        <p className="text-sm text-text-muted italic">Analyzing data...</p>
      )}

      {error && (
        <p className="text-sm text-state-error">{error}</p>
      )}

      {!loading && insights.length === 0 && !error && (
        <p className="text-sm text-text-muted">No insights available. More data needed.</p>
      )}

      <div className="space-y-3">
        {insights.map((insight, idx) => {
          const cfg = SEVERITY_CONFIG[insight.severity] || SEVERITY_CONFIG.info;
          const Icon = cfg.icon;
          return (
            <div 
              key={idx} 
              className={`border-l-4 ${cfg.color} rounded-md p-3`}
              data-testid={`insight-${insight.type || idx}`}
            >
              <div className="flex items-start space-x-2">
                <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${cfg.textColor}`} />
                <div className="flex-1">
                  <h4 className={`font-semibold text-sm mb-1 ${cfg.textColor}`}>{insight.title}</h4>
                  <p className="text-sm text-text-primary leading-relaxed">{insight.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
